import { useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useUIStore, type MailView } from '@/stores/uiStore';
import { useMailViewBadgesStore } from '@/stores/mailViewBadgesStore';
import { useBriefingStore } from '@/stores/briefingStore';
import { navigateToMailView } from './mailViewNavigation';

// "Inbox", not "Mailbox": with the app switcher naming the app ("Mail"),
// the first tab names the view — Mail › Inbox reads as one sentence.
export const MAIL_VIEWS: Array<{ view: MailView; label: string }> = [
  { view: 'mailbox', label: 'Inbox' },
  { view: 'briefing', label: 'Briefing' },
  { view: 'surveys', label: 'Surveys' },
  { view: 'triage', label: 'Triage' },
  { view: 'subscriptions', label: 'Subs' },
];

// Briefing's count is informational (items in today's briefing); the surveys
// count is a call to action.
const BADGE_TONES: Partial<Record<MailView, string>> = {
  briefing: 'bg-primary/10 text-primary',
};
const DEFAULT_BADGE_TONE = 'bg-danger/10 text-danger-ink';

export function useMailTabState() {
  const mailView = useUIStore((state) => state.mailView);
  const surveysToReview = useMailViewBadgesStore((state) => state.surveysToReview);
  const refresh = useMailViewBadgesStore((state) => state.refresh);
  const briefingEntries = useBriefingStore((state) => state.entries);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const briefingCount = useMemo(
    () => briefingEntries?.reduce((sum, entry) => sum + entry.items.length, 0) ?? 0,
    [briefingEntries]
  );

  const badges: Partial<Record<MailView, number>> = {
    briefing: briefingCount,
    surveys: surveysToReview,
  };
  return { mailView, badges };
}

function TabsCore() {
  const { mailView, badges } = useMailTabState();
  return (
    <Tabs
      value={mailView}
      onValueChange={(value) => navigateToMailView(value as MailView)}
      className="min-w-0 gap-0"
    >
      <TabsList className="gap-0.5 rounded-full p-1">
        {MAIL_VIEWS.map(({ view, label }) => (
          <TabsTrigger
            key={view}
            value={view}
            className={cn(
              'rounded-full px-3 text-muted-foreground hover:text-foreground',
              'data-[state=active]:bg-card data-[state=active]:text-primary',
              'dark:data-[state=active]:bg-card dark:data-[state=active]:text-primary',
              'dark:data-[state=active]:border-transparent'
            )}
          >
            {label}
            {badges[view] ? (
              <span
                className={cn(
                  'rounded-full px-1.5 text-[10px] font-medium leading-4',
                  BADGE_TONES[view] ?? DEFAULT_BADGE_TONE
                )}
              >
                {badges[view]}
              </span>
            ) : null}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

/**
 * The mail sub-view tab strip — rendered by the full-width WorkspaceHeader
 * beside the app pill. Hidden on mobile (the dock carries the tabs there).
 */
export function MailViewTabs({ className }: { className?: string }) {
  return (
    <div className={cn('hidden min-w-0 items-center md:flex', className)}>
      <TabsCore />
    </div>
  );
}
